import { z } from 'zod'

/**
 * Account details for onboarding a VM-only customer during VServer import.
 * Mirrors the backend importservice.AccountDetails. Used when no existing
 * account_id is selected.
 */
export const VServerImportAccountDetails = z.object({
  email: z.string().email(),
  name: z.string().optional(),
  surname: z.string().optional(),
  org: z.string().optional(),
  line1: z.string().min(1),
  line2: z.string().optional(),
  city: z.string().min(1),
  postcode: z.string().min(1),
  country_iso: z.string().length(2),
  state: z.string().optional(),
  phone: z.string().min(1)
})
export type VServerImportAccountDetails = z.infer<typeof VServerImportAccountDetails>

/** Request body for POST /admin/vserver/import. */
export const ImportVServerRequest = z.object({
  account_id: z.number().int().optional(),
  account: VServerImportAccountDetails.optional(),
  stripe_customer_id: z.string().optional(),
  cancel_stripe_subscription_id: z.string().optional(),

  proxmox_node: z.string().min(1),
  proxmox_vmid: z.number().int(),
  ipv4: z.string().min(1),
  subnet: z.string().optional(),
  gateway: z.string().optional(),

  monthly_gross_cents: z.number().int().positive().max(1_000_000),
  billing_freq: z.enum(['monthly', 'annually']).optional(),
  hostname: z.string().optional(),
  template_slug: z.string().optional(),

  vcpu: z.number().int().optional(),
  memory_mb: z.number().int().optional(),
  disk_gb: z.number().int().optional()
})
export type ImportVServerRequest = z.infer<typeof ImportVServerRequest>

/** Response body for POST /admin/vserver/import. */
export const ImportVServerResult = z.object({
  vserver_id: z.number().int(),
  subscription_id: z.number().int(),
  account_id: z.number().int(),
  stripe_subscription_id: z.string().optional(),
  price_cents: z.number().int(),
  trial_end: z.string(),
  created_account: z.boolean().optional(),
  existing: z.boolean().optional(),
  canceled_stripe_subscription: z.string().optional(),
  other_active_stripe_subscriptions: z.array(z.string()).optional()
})
export type ImportVServerResult = z.infer<typeof ImportVServerResult>

/** One Proxmox cluster node, as returned by GET /admin/vserver/nodes. */
export const VServerNode = z.object({
  name: z.string(),
  status: z.string()
})
export type VServerNode = z.infer<typeof VServerNode>

export const VServerNodeList = z.array(VServerNode)

/** Minimal account row for the existing-account picker (/account/all). */
export const VServerImportAccountOption = z.object({
  id: z.number().int(),
  email: z.string()
})
export type VServerImportAccountOption = z.infer<typeof VServerImportAccountOption>
